import { useQuery } from '@tanstack/react-query';
import type { HeartRateHistory } from '@/features/vitals';
import { type LiveResource, toLiveResource } from '@/data/liveResource';
import { config } from './config';

interface VitalsState {
  heartRate: number;
  heartRateHistory: HeartRateHistory;
  restingHeartRate: number;
  oxygenSaturation: number; // 0..1 fraction
  steps: number;
  activeEnergy: number; // kcal
  lastSync: Date;
  source: string;
}

type HeartRateHistoryFetched = {
  date: string;
  value: number;
}[];

const fetchVitalsData = async (): Promise<Partial<VitalsState>> => {
  const res = await fetch(config.url);
  const data = await res.json();

  const resultHeartRate = data.heartRate;
  const resultHeartRateHistory = (
    data.heartRate1HourHistory as HeartRateHistoryFetched
  ).map((item) => ({
    date: new Date(item.date),
    value: item.value,
  }));
  const resultLastSync = new Date(data.timestamp).getTime();

  return {
    heartRate: resultHeartRate,
    heartRateHistory: resultHeartRateHistory.map(
      (item) =>
        ({
          date: new Date(item.date),
          value: item.value,
        }) as HeartRateHistory[number]
    ),
    restingHeartRate: data.restingHeartRate,
    oxygenSaturation: data.oxygenSaturation,
    steps: data.steps,
    activeEnergy: data.activeEnergy,
    lastSync: new Date(resultLastSync),
    source: data.source,
  };
};

export const useVitals = (): LiveResource<Partial<VitalsState>> => {
  const query = useQuery({
    queryKey: ['vitalsData'],
    queryFn: fetchVitalsData,
    refetchInterval: config.refetchMiliseconds,
  });
  return toLiveResource(query, {
    source: (d) => d.source,
    freshAt: (d) => d.lastSync,
    staleAfterMs: config.staleAfterMiliseconds,
  });
};
